import { TablePaginationConfig } from "antd";
import { useCallback } from "react";
import { NumberParam, useQueryParam, withDefault } from "use-query-params";
import { PaginationConfig } from "antd/es/pagination";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

type PaginationOptions<T> = {
  page?: number;
  pageSize?: number;
  filter?: T;
};

function usePageState(initialPage?: number): [number, (page: number) => void] {
  const PageParam = withDefault(NumberParam, initialPage ?? 1);
  const [queryPage, setQueryPage] = useQueryParam("page", PageParam, {
    removeDefaultsFromUrl: true,
  });

  return [queryPage, setQueryPage];
}

function usePageSizeState(
  initialPage?: number,
): [number, (page: number) => void] {
  const PageSizeParam = withDefault(NumberParam, initialPage ?? 1);
  const [queryPage, setQueryPage] = useQueryParam("pageSize", PageSizeParam, {
    removeDefaultsFromUrl: true,
  });

  return [queryPage, setQueryPage];
}

export function usePagination<T>(paginationOptions?: PaginationOptions<T>) {
  const [page, setPage] = usePageState(paginationOptions?.page);

  const [pageSize, setPageSize] = usePageSizeState(
    paginationOptions?.pageSize ?? DEFAULT_PAGE_SIZE,
  );

  const resetPage = useCallback(() => {
    setPage(DEFAULT_PAGE);
  }, [setPage]);

  const getTablePaginationConfig = (total?: number): TablePaginationConfig => ({
    total,
    pageSize,
    current: page,
    onChange: (page) => setPage(page),
    showSizeChanger: true,
    onShowSizeChange: (_, pageSize) => setPageSize(pageSize),
  });

  const getPaginationConfig = (total?: number): PaginationConfig => ({
    total,
    pageSize,
    current: page,
    onChange: (page) => setPage(page),
    onShowSizeChange: (_, pageSize) => setPageSize(pageSize),
    hideOnSinglePage: false,
    showSizeChanger: true,
  });

  return {
    pageRequest: {
      page,
      pageSize,
    },
    getTablePaginationConfig,
    getPaginationConfig,
    setPage,
    setPageSize,
    resetPage,
  };
}
